import type { TokenPair } from '@boardflight/shared';

/**
 * The console's API client.
 *
 * Deliberately not a copy of `apps/web/src/api.ts`. That one carries a configurable base URL, a
 * refresh queue shared with a device-code flow, beacons and its own blob downloads, because the dashboard
 * talks to a different origin and does a great deal more. This talks to **its own origin** — the
 * Worker serving this page also answers `/v1/*` — so there is no base URL, no CORS and no
 * cross-origin token handling to get wrong.
 */

const STORAGE_KEY = 'boardflight.admin.session';

export interface Session {
  accessToken: string;
  accessTokenExpiresAt: number;
  refreshToken: string;
  email: string;
  userId: string;
  /** Present only for a project admin — the server never sends the key otherwise. */
  admin?: true;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const listeners = new Set<(session: Session | null) => void>();

export function loadSession(): Session | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Session) : null;
  } catch {
    return null;
  }
}

export function onSessionChange(listener: (session: Session | null) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function store(session: Session | null): void {
  if (session) localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  else localStorage.removeItem(STORAGE_KEY);
  listeners.forEach((listener) => listener(session));
}

function sessionFrom(tokens: TokenPair): Session {
  return {
    accessToken: tokens.accessToken,
    accessTokenExpiresAt: tokens.accessTokenExpiresAt,
    refreshToken: tokens.refreshToken,
    email: tokens.user.email,
    userId: tokens.user.id,
    // Spread, so a non-admin's stored session has no `admin` key rather than `admin: false`.
    ...(tokens.user.admin ? { admin: true as const } : {}),
  };
}

async function raise(response: Response): Promise<never> {
  let code = 'error';
  let message = `Request failed (${response.status}).`;
  try {
    const body = (await response.json()) as { error?: { code?: string; message?: string; details?: unknown } };
    code = body.error?.code ?? code;
    message = body.error?.message ?? message;
    // A validation refusal names its fields in `details`; say which, or the operator has to guess.
    const details = body.error?.details;
    if (Array.isArray(details)) {
      const fields = details
        .filter((issue): issue is { path?: string; message: string } => typeof issue?.message === 'string')
        .map((issue) => (issue.path ? `${issue.path}: ${issue.message}` : issue.message))
        .join(' ');
      if (fields) message = `${message} ${fields}`;
    }
  } catch {
    /* A body that is not our error shape — keep the status-derived message. */
  }
  throw new ApiError(response.status, code, message);
}

/**
 * Sign in against this origin.
 *
 * The same handler the dashboard posts to, mounted on this Worker (§D3a) — so the session this
 * produces is an ordinary session and the password is checked in exactly one place in the system.
 */
export async function signIn(email: string, password: string): Promise<Session> {
  const response = await fetch('/v1/auth/login', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email, password, kind: 'web', label: 'Admin console' }),
  });
  if (!response.ok) await raise(response);

  const session = sessionFrom((await response.json()) as TokenPair);
  store(session);
  return session;
}

export async function signOut(): Promise<void> {
  const session = loadSession();
  store(null);
  if (!session) return;
  await fetch('/v1/auth/logout', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ refreshToken: session.refreshToken }),
  }).catch(() => undefined);
}

let refreshing: Promise<string | null> | null = null;

/**
 * Trade the refresh token for a new pair, once at a time.
 *
 * Single-flight because the console fires several reads on every screen: without it, two 401s in
 * the same tick would both rotate, and the second would present a token the first had already
 * spent — which the API correctly refuses, signing the admin out mid-page.
 */
async function refresh(): Promise<string | null> {
  refreshing ??= (async () => {
    const session = loadSession();
    if (!session) return null;
    try {
      const response = await fetch('/v1/auth/refresh', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refreshToken: session.refreshToken }),
      });
      if (!response.ok) {
        store(null);
        return null;
      }
      const next = sessionFrom((await response.json()) as TokenPair);
      store(next);
      return next.accessToken;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await authorized(path, init);
  return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
}

/**
 * A file the console asked this Worker for, handed to the browser to save.
 *
 * The responses here that are not JSON — the new-game emails export (`docs/new-game-emails.md`),
 * a figure's print files. A plain link cannot carry the bearer token, so the bytes are fetched like
 * any other call and saved from a blob; the name is the one the Worker put in
 * `content-disposition`.
 */
export async function download(path: string, init: RequestInit = {}): Promise<void> {
  const file = await fetchFile(path, init);
  saveFile(file);
}

/** A file's bytes as this Worker sent them, with the name it gave and the type it said. */
export interface FetchedFile {
  name: string;
  type: string;
  blob: Blob;
}

/**
 * A file this Worker serves only with the session, held in memory rather than saved: what the
 * figures review previews, so that showing a model and then saving it is one request, not two.
 */
export async function fetchFile(path: string, init: RequestInit = {}): Promise<FetchedFile> {
  const response = await authorized(path, init);
  const disposition = response.headers.get('content-disposition') ?? '';
  const name = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'download';
  const blob = await response.blob();
  return { name, type: response.headers.get('content-type') ?? blob.type, blob };
}

/** Hand a file already in memory to the browser to save, under its own name. */
export function saveFile(file: FetchedFile): void {
  const url = URL.createObjectURL(file.blob);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = file.name;
    link.click();
  } finally {
    // After the click has started the save; revoking first can cancel it in some browsers.
    setTimeout(() => URL.revokeObjectURL(url), 1_000);
  }
}

/** One authenticated call, refreshed once on a 401, and raised on anything else that is not OK. */
async function authorized(path: string, init: RequestInit): Promise<Response> {
  const session = loadSession();
  if (!session) throw new ApiError(401, 'unauthorized', 'Not signed in.');

  const send = (token: string) =>
    fetch(path, {
      ...init,
      headers: {
        ...init.headers,
        authorization: `Bearer ${token}`,
        // JSON unless the caller named the type, as `upload` does for a file.
        ...(init.body && !new Headers(init.headers).has('content-type') ? { 'content-type': 'application/json' } : {}),
      },
    });

  let response = await send(session.accessToken);
  if (response.status === 401) {
    const token = await refresh();
    if (!token) throw new ApiError(401, 'unauthorized', 'That session has ended. Sign in again.');
    response = await send(token);
  }

  if (!response.ok) await raise(response);
  return response;
}

export const post = <T,>(path: string, body: unknown): Promise<T> =>
  api<T>(path, { method: 'POST', body: JSON.stringify(body) });

export const put = <T,>(path: string, body: unknown): Promise<T> =>
  api<T>(path, { method: 'PUT', body: JSON.stringify(body) });

/** A file sent as the request body, under its own type: a set's photo, the TPU snap piece's 3MF. */
export const upload = <T,>(path: string, file: File, contentType: string): Promise<T> =>
  api<T>(path, { method: 'PUT', body: file, headers: { 'content-type': contentType } });

/**
 * An image this Worker serves only with the session, as an object URL an `<img>` can show: a set's
 * photo. The caller revokes it.
 */
export async function blobUrl(path: string): Promise<string> {
  return URL.createObjectURL(await (await authorized(path, {})).blob());
}
